import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Select, Button, Row, Col, Checkbox, InputNumber, Divider, Typography, Card, Space, List, Spin, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper';
import SprayJobCreateWrapper from './SprayJobCreate.styles';
import { assetService, sprayService, userService, weatherDataService } from '@iso/services';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PermissionTaskModal from './PermissionTaskModal';
import TemplateNameModal from './TemplateNameModal';
import UseExistingTemplateModal from './UseExistingTemplateModal';
import { JOB_TYPES } from '@iso/constants/jobTypes';
import { FORMULATION_TYPES, getFormulationLabel } from '@iso/constants/formulationTypes';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import SortableItem from './SortableItem';

const { Option } = Select;

/**
 * Stable per-item IDs for the zone / chemical drag-and-drop. Previous
 * implementation used positional IDs (`zone-0`, `chem-0`) which meant
 * dnd-kit's SortableContext saw the same id at the same index after a
 * reorder and animated dropped cards back to their original slot.
 * `_dndId` is generated on add (and ensured for items loaded via edit /
 * template) so each row keeps its identity through reorders. Stripped
 * before sending to the backend so the marker never leaks into the
 * persisted JSON. Module-scoped so they aren't recreated on every
 * render of the (large) component body.
 */
const makeDndId = () =>
  `_dnd_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const ensureDndId = (item) =>
  item && item._dndId ? item : { ...(item || {}), _dndId: makeDndId() };
const stripDndIds = (arr) =>
  Array.isArray(arr)
    ? arr.map((item) => {
        if (!item || typeof item !== 'object') return item;
        const { _dndId, ...rest } = item;
        return rest;
      })
    : arr;

/**
 * Derive { quantity, rate_unit } for the spray-job chemical row from the
 * underlying asset's saved chemical rate. The asset model stores `rate`
 * as a treatment-rates key (e.g. `g_per_100l`, `ml_per_100l`, `other`)
 * and `other_rate` as free text when the operator picks "Other". The
 * spray form's rate_unit dropdown only accepts `g` or `mL`.
 *
 * Returns null when no inference is possible (operator fills the row
 * themselves).
 */
const deriveSprayRateFromAsset = (asset) => {
  if (!asset) return null;
  if (asset.rate === 'g_per_100l') return { rate_unit: 'g' };
  if (asset.rate === 'ml_per_100l') return { rate_unit: 'mL' };
  if (asset.rate === 'other' && typeof asset.other_rate === 'string') {
    // Try to read a "<number><unit>" pattern out of e.g. "40mls/100L".
    const m = asset.other_rate.match(/(\d+(?:\.\d+)?)\s*(mls?|ml|g|kg)/i);
    if (m) {
      const qty = Number(m[1]);
      const unitRaw = m[2].toLowerCase();
      // mL is the volume unit the spray form supports; collapse all
      // ml/mls into it. Mass: 'g' direct, 'kg' converts to g (×1000) so
      // the operator sees an actionable number.
      let rate_unit = '';
      let quantity = qty;
      if (unitRaw.startsWith('ml')) rate_unit = 'mL';
      else if (unitRaw === 'g') rate_unit = 'g';
      else if (unitRaw === 'kg') {
        rate_unit = 'g';
        quantity = qty * 1000;
      }
      return rate_unit ? { quantity, rate_unit } : null;
    }
  }
  return null;
};

/**
 * Whether an element is a left/right arrow-key navigation target for
 * the create-form's form-wide arrow-key handler. Pure — no captured
 * state — so it lives at module scope and can be reused safely inside
 * a useCallback-memoised handler without re-creating per render.
 *
 * Returns true for plain text / number inputs that own a single-line
 * text cursor, and skips:
 *   - antd Select / DatePicker / Cascader (own keyboard model)
 *   - disabled / readonly / hidden inputs
 *   - non-text-cursor types (checkbox, radio, file, submit, button)
 */
const isFormNavTarget = (el) => {
  if (!el || el.tagName !== 'INPUT') return false;
  if (el.disabled || el.readOnly) return false;
  if (el.type === 'hidden' || el.type === 'checkbox' || el.type === 'radio'
    || el.type === 'file' || el.type === 'submit' || el.type === 'button') return false;
  if (typeof el.selectionStart !== 'number') return false;
  if (el.closest('.ant-select') || el.closest('.ant-picker')) return false;
  return true;
};

const SprayJobCreate = () => {
  const { id: editJobId } = useParams();
  const isEditMode = !!editJobId;
  const propertyId = useSelector(
    (state) => state.property.activePropertyId
  );

  const history = useHistory();
  const [form] = Form.useForm();
  const [zones, setZones] = useState([ensureDndId({
    startLetter: '', startNumber1: '', startNumber2: '',
    finishLetter: '', finishNumber1: '', finishNumber2: '',
    comment: ''
  })]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chemicalAssets, setChemicalAssets] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [tractors, setTractors] = useState([]);
  const [sprayers, setSprayers] = useState([]);
  // Users on the active property — populates the "Responsible" dropdown so
  // the spray-job creator can optionally hand responsibility to someone else.
  // If they leave it blank, we default to the current user on save.
  const [propertyUsers, setPropertyUsers] = useState([]);
  const currentUserId = (() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored)?.id ?? null : null;
    } catch (e) {
      return null;
    }
  })();
  const [chemicals, setChemicals] = useState([ensureDndId({ chemical_id: '', quantity: '', rate_unit: '' })]);
  const [isPermissionTaskModalVisible, setIsPermissionTaskModalVisible] = useState(false);
  const [isTemplateNameModalVisible, setIsTemplateNameModalVisible] = useState(false);
  const [isUseTemplateModalVisible, setIsUseTemplateModalVisible] = useState(false);
  const [pendingSprayJobData, setPendingSprayJobData] = useState(null);
  const [pendingPermissionTaskData, setPendingPermissionTaskData] = useState(null);
  const [isCustomizingTemplate, setIsCustomizingTemplate] = useState(false);
  const [customizingTemplateId, setCustomizingTemplateId] = useState(null);

  // Get chemical list (category = "inventory", type = "chemical")
  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      assetService.getList(propertyId, { category: 'inventory', type: 'chemical' })
        .then(res => {
          if (res && res.facilities) {
            setChemicalAssets(res.facilities);
          }
        })
        .catch(err => console.error('Error fetching chemicalAssets:', err))
        .finally(() => setLoading(false));
    }
  }, [propertyId]);

  // Get nozzle list (category = "spray", type = "spray_nozzle")
  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      assetService.getList(propertyId, { category: 'spray', type: 'spray_nozzle' })
        .then(res => {
          if (res && res.facilities) {
            setNozzles(res.facilities);
          }
        })
        .catch(err => console.error('Error fetching nozzles:', err))
        .finally(() => setLoading(false));
    }
  }, [propertyId]);

  // Get tractor list (category = "machinery")
  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      assetService.getList(propertyId, { category: 'machinery' })
        .then(res => {
          if (res && res.facilities) {
            setTractors(res.facilities);
          }
        })
        .catch(err => console.error('Error fetching tractors:', err))
        .finally(() => setLoading(false));
    }
  }, [propertyId]);

  // Get sprayer list (category = "spray", type = "spray_unit")
  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      assetService.getList(propertyId, { category: 'spray', type: 'spray_unit' })
        .then(res => {
          if (res && res.facilities) {
            setSprayers(res.facilities);
          }
        })
        .catch(err => console.error('Error fetching sprayers:', err))
        .finally(() => setLoading(false));
    }
  }, [propertyId]);

  // Property users — for the Responsible dropdown.
  useEffect(() => {
    if (!propertyId) return;
    userService.getUserForProperty(propertyId)
      .then(res => {
        if (res?.users) setPropertyUsers(res.users);
      })
      .catch(err => console.error('Error fetching property users:', err));
  }, [propertyId]);

  // Realtime weather seed for Delta T + Temperature limits on new jobs.
  // This runs alongside the settings-based defaults below; whichever
  // produces values for empty fields wins, with realtime weather acting as
  // the fallback when the property hasn't configured temperature/humidity
  // defaults (the previous seed required both, so a property that only
  // saved Wind / Rain defaults would end up with empty Delta T fields —
  // that's the bug stakeholders reported).
  useEffect(() => {
    if (!propertyId || isEditMode) return;
    let cancelled = false;
    weatherDataService.getRealtimeWeatherData(propertyId)
      .then((res) => {
        if (cancelled) return;
        const values = res?.data?.values;
        if (!values) return;
        const current = form.getFieldsValue([
          'temperature_min', 'temperature_max',
        ]);
        const next = {};
        // Delta T is now a manual input (Damien): the operator sets
        // the safe spraying Delta T range on the create form rather
        // than us deriving it. The Zone-Start gate still uses Stull
        // at spray time to check the live reading against the
        // operator-set min/max, but we no longer seed the limits
        // from current weather here.
        if (values.temperature != null) {
          if (current.temperature_min == null) {
            next.temperature_min = Math.round((values.temperature - 5) * 10) / 10;
          }
          if (current.temperature_max == null) {
            next.temperature_max = Math.round((values.temperature + 5) * 10) / 10;
          }
        }
        if (Object.keys(next).length > 0) {
          form.setFieldsValue(next);
        }
      })
      .catch((err) => console.error('SprayJobCreate: realtime weather seed failed:', err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, isEditMode]);

  // Load spray settings defaults for new jobs
  useEffect(() => {
    if (propertyId && !isEditMode) {
      sprayService.getSettings(propertyId)
        .then(res => {
          if (res && res.settings) {
            const s = res.settings;
            // Pre-fill PPE defaults
            const prepPPE = s.ppe_defaults_preparation?.setting_value || [];
            const sprayPPE = s.ppe_defaults_spraying?.setting_value || [];
            if (prepPPE.length > 0 || sprayPPE.length > 0) {
              setPendingPermissionTaskData(prev => ({
                ...prev,
                ppeRequirements: { preparation: prepPPE, spraying: sprayPPE },
              }));
            }
            // Pre-fill environmental limit defaults
            const env = s.environmental_defaults?.setting_value;
            if (env) {
              const fieldsToSet = {};
              if (env.wind_min != null) fieldsToSet.wind_limit_min = env.wind_min;
              if (env.wind_max != null) fieldsToSet.wind_limit_max = env.wind_max;
              if (env.rain != null) fieldsToSet.rain_limit = env.rain;
              // Temperature: new shape is min/max, but accept legacy
              // singular `temperature` as a ±5°C window for back-compat
              // with pre-upgrade saved settings.
              const tempMin = env.temperature_min ?? (env.temperature != null ? env.temperature - 5 : null);
              const tempMax = env.temperature_max ?? (env.temperature != null ? env.temperature + 5 : null);
              if (tempMin != null) fieldsToSet.temperature_min = Math.round(tempMin * 10) / 10;
              if (tempMax != null) fieldsToSet.temperature_max = Math.round(tempMax * 10) / 10;
              // Delta T: pre-fill from the agronomist-set defaults if
              // present. The operator can still override on the form.
              // Stull-derivation moved out — Delta T is a manual input
              // now (Damien).
              if (env.delta_t_min != null) fieldsToSet.delta_t_limit_min = env.delta_t_min;
              if (env.delta_t_max != null) fieldsToSet.delta_t_limit_max = env.delta_t_max;
              if (Object.keys(fieldsToSet).length > 0) {
                form.setFieldsValue(fieldsToSet);
              }
            }
          }
        })
        .catch(err => console.error('Error loading spray settings:', err));
    }
  }, [propertyId, isEditMode]);

  // Load existing job data for edit mode
  useEffect(() => {
    if (isEditMode && editJobId) {
      setLoading(true);
      sprayService.getDetail(editJobId)
        .then(res => {
          if (res && res.spray_job) {
            const job = res.spray_job;
            // Populate form fields
            form.setFieldsValue({
              job_name: job.job_name,
              job_type: job.job_type,
              order_number: job.order_number,
              responsible_id: job.responsible_id ?? undefined,
              tractor_id: job.tractor_id,
              sprayer_id: job.sprayer_id,
              nozzle_id: job.nozzle_id,
              tractor_setting: job.tractor_setting,
              sprayer_setting: job.sprayer_setting,
              wind_limit_min: job.wind_limit_min,
              wind_limit_max: job.wind_limit_max,
              delta_t_limit_min: job.delta_t_limit_min,
              delta_t_limit_max: job.delta_t_limit_max,
              temperature_min: job.temperature_min,
              temperature_max: job.temperature_max,
              humidity_limit_min: job.humidity_limit_min,
              humidity_limit_max: job.humidity_limit_max,
              rain_limit: job.rain_limit,
              water_quantity: job.water_quantity,
              refill_planned: !!job.refill_planned,
              total_spray_volume: job.total_spray_volume,
              zones: job.zones,
              chemicals: job.chemicals?.map(c => ({
                chemical_id: c.chemical_id,
                quantity: c.quantity,
                rate_unit: c.rate_unit || '',
              })),
            });
            // Set state arrays — stamp each with a stable _dndId so the
            // reorder UI can track identity across drag operations.
            if (job.zones && Array.isArray(job.zones)) {
              setZones(job.zones.map(ensureDndId));
            }
            if (job.chemicals && Array.isArray(job.chemicals)) {
              setChemicals(job.chemicals.map(c => ensureDndId({
                chemical_id: c.chemical_id,
                quantity: c.quantity,
                rate_unit: c.rate_unit || '',
              })));
            }
            // Set PPE/task data for modal
            if (job.ppe_requirements || job.tasks) {
              setPendingPermissionTaskData({
                ppeRequirements: job.ppe_requirements || [],
                tasks: job.tasks || [{ task_name: '', mapping_status: '' }],
              });
            }
          }
        })
        .catch(err => {
          console.error('Error loading spray job for edit:', err);
          message.error('Failed to load spray job for editing. Please refresh the page.');
        })
        .finally(() => setLoading(false));
    }
  }, [editJobId, isEditMode]);

  // Refs for the Field <Input> of each zone — used to autofocus the newly
  // added zone so the operator can type the field code without an extra click.
  const zoneFieldRefs = useRef({});

  // Add and remove zones / refills. Zones state is now a heterogeneous
  // ordered list of zone entries and refill entries (refill entries are
  // tagged with `type: 'refill'`). The drag-and-drop list spans both —
  // each refill tile's position between zones marks where the tank-out
  // event is planned to happen.
  // Generalised left/right arrow-key navigation across every text /
  // number input on the create-spray-job form. Attached at the Form
  // root so a single handler covers Job Name, Order Number, Initial
  // Fill, every zone's FIELD/BENCH/ROW/ROW, every chemical's rate,
  // tractor/sprayer settings, environmental limits, etc.
  //
  // Behaviour:
  //   - Right arrow advances to the next focusable input in DOM
  //     (visual reading) order; left arrow goes back. The "next row
  //     if no other input is to the right" requirement falls out of
  //     DOM order naturally — antd's Row/Col renders columns in
  //     reading order, so the next sibling input is either the next
  //     Col in the same Row or the first Col in the next Row.
  //   - Only fires when the cursor is at the matching edge of the
  //     current input value (or the input is empty), so normal
  //     text-cursor movement inside a value is unchanged.
  //   - Skips controls where left/right arrow has its own meaning:
  //       * antd Select / DatePicker / Cascader (own keyboard model)
  //       * textareas (within-text cursor)
  //       * disabled / readonly / hidden inputs
  //       * non-text-cursor inputs (checkbox, radio, file, etc.)
  // Wrapped in useCallback with no captured state so the prop
  // identity passed to <Form onKeyDown={...}> is stable across
  // renders (PR #459 / #460 review suggestion 3). isFormNavTarget
  // is pure and lives at module scope further up the file.
  const handleFormArrowNav = useCallback((e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const el = e.target;
    if (!isFormNavTarget(el)) return;
    const isEmpty = !el.value;
    const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
    const atEnd = el.selectionStart === el.value.length
      && el.selectionEnd === el.value.length;
    const wantPrev = e.key === 'ArrowLeft' && (isEmpty || atStart);
    const wantNext = e.key === 'ArrowRight' && (isEmpty || atEnd);
    if (!wantPrev && !wantNext) return;
    // Collect every nav-target input inside the form in DOM order.
    // currentTarget is the <form> element (handler is attached at the
    // Form root).
    const root = e.currentTarget;
    if (!root) return;
    const candidates = Array.from(root.querySelectorAll('input'))
      .filter(isFormNavTarget);
    const idx = candidates.indexOf(el);
    if (idx < 0) return;
    const target = candidates[idx + (wantNext ? 1 : -1)];
    if (target) {
      e.preventDefault();
      target.focus();
      if (typeof target.select === 'function') target.select();
    }
  }, []);

  const addZone = () => {
    const newZones = [...zones, ensureDndId({
      startLetter: '', startNumber1: '', startNumber2: '',
      finishLetter: '', finishNumber1: '', finishNumber2: '',
      comment: ''
    })];
    setZones(newZones);
    form.setFieldsValue({ zones: newZones });
    // Focus the Field input on the new zone after it renders
    const newIndex = newZones.length - 1;
    setTimeout(() => {
      const ref = zoneFieldRefs.current[newIndex];
      if (ref && typeof ref.focus === 'function') ref.focus();
    }, 50);
  };

  const addRefill = () => {
    const newZones = [...zones, ensureDndId({ type: 'refill', liters: null })];
    setZones(newZones);
    form.setFieldsValue({ zones: newZones });
  };

  const removeZone = (index) => {
    const updatedZones = zones.filter((_, i) => i !== index);
    setZones(updatedZones);
    form.setFieldsValue({ zones: updatedZones });
  };

  // @dnd-kit handlers for drag-to-reorder (#16 — Damien)
  // Drag sensors for zones + chemicals.
  // - PointerSensor: 6px activation distance — small enough to feel
  //   responsive, large enough that an accidental click on the handle
  //   doesn't trigger a drag.
  // - TouchSensor: 250ms long-press delay so the operator can scroll the
  //   form on touch without accidentally picking up a card.
  // - KeyboardSensor: makes Tab + Space + arrow keys work for a11y.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Drag IDs are now the per-item `_dndId` (not positional). We look up by
  // identity so dnd-kit correctly perceives the array's new order on drop —
  // positional IDs were causing the post-drop animation to snap back to
  // the starting slot because the IDs never changed across the reorder.
  // Set briefly while a drag-end handler is calling form.setFieldsValue.
  // handleFormValuesChange checks this flag and skips its positional
  // _dndId merge — otherwise React's batching can cause the merge to run
  // with a stale `prev`, silently swapping the drag identifiers and
  // re-breaking subsequent drags (the failure mode is a card snapping
  // back on the *second* drop).
  const dragSyncRef = useRef(false);
  const withDragSync = (fn) => {
    dragSyncRef.current = true;
    try {
      fn();
    } finally {
      // Clear after the current microtask so any synchronous
      // onValuesChange triggered by setFieldsValue sees the flag.
      queueMicrotask(() => {
        dragSyncRef.current = false;
      });
    }
  };

  const handleZoneDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = zones.findIndex((z) => z._dndId === active.id);
    const newIndex = zones.findIndex((z) => z._dndId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(zones, oldIndex, newIndex);
    withDragSync(() => {
      setZones(next);
      form.setFieldsValue({ zones: next });
    });
  };
  const handleChemicalDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setChemicals((prev) => {
      const oldIndex = prev.findIndex((c) => c._dndId === active.id);
      const newIndex = prev.findIndex((c) => c._dndId === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      withDragSync(() => {
        form.setFieldsValue({ chemicals: next });
      });
      return next;
    });
  };

  // Add and remove chemicals
  const addChemical = () => {
    setChemicals([...chemicals, ensureDndId({ chemical_id: '', quantity: '', rate_unit: '' })]);
  };

  const removeChemical = (index) => {
    const updatedChemicals = chemicals.filter((_, i) => i !== index);
    setChemicals(updatedChemicals);
    form.setFieldsValue({ chemicals: updatedChemicals });
  };

  const handleContinue = () => {
    form.validateFields().then(values => {
      // Store form data for later use
      setPendingSprayJobData(values);
      // Always show Permission & Task Details modal first
      setIsPermissionTaskModalVisible(true);
    }).catch(error => {
      console.error('Form validation failed:', error);
      message.error('Please check the form for errors');
    });
  };

  const handleUseExistingTemplate = () => {
    setIsUseTemplateModalVisible(true);
  };

  const handleUseTemplate = (template) => {
    // Get spray job data from template
    const sprayJobData = template.spray_job_data || {};

    // Backward compatibility: map old single-value env limits to new range fields
    if (sprayJobData.wind_limit && !sprayJobData.wind_limit_max) {
      sprayJobData.wind_limit_max = sprayJobData.wind_limit;
    }
    if (sprayJobData.delta_t_limit && !sprayJobData.delta_t_limit_max) {
      sprayJobData.delta_t_limit_max = sprayJobData.delta_t_limit;
    }

    // Fill form with template data
    const templateData = {
      ...sprayJobData,
      save_as_template: false
    };

    form.setFieldsValue(templateData);
    setZones(
      (sprayJobData.zones && sprayJobData.zones.length > 0
        ? sprayJobData.zones
        : [{
            startLetter: '', startNumber1: '', startNumber2: '',
            finishLetter: '', finishNumber1: '', finishNumber2: '',
            comment: ''
          }]
      ).map(ensureDndId)
    );
    setChemicals(
      (sprayJobData.chemicals && sprayJobData.chemicals.length > 0
        ? sprayJobData.chemicals
        : [{ chemical_id: '', quantity: '', rate_unit: '' }]
      ).map(ensureDndId)
    );

    // Set permission & task data - always set to ensure modal gets the data
    setPendingPermissionTaskData({
      ppeRequirements: sprayJobData.ppe_requirements || [],
      tasks: sprayJobData.tasks || [{ task_name: '', mapping_status: '' }]
    });

    setIsUseTemplateModalVisible(false);
  };

  const handleCustomizeTemplate = (template) => {
    // Mark that we're customizing a template
    setIsCustomizingTemplate(true);
    setCustomizingTemplateId(template.id);

    // Load template data into form
    handleUseTemplate(template);

    // Show message to user that they're editing the template
    message.info('You are now editing the template. Changes will be saved to the template.');
  };

  const handleTemplateNameModalSave = () => {
    setIsTemplateNameModalVisible(false);
    // Continue with spray job creation - submit the form
    form.submit();
  };

  const handlePermissionTaskModalSave = (data) => {
    // Check data before setting
    const fieldsToSet = {};

    if (data.ppeRequirements) {
      // New format: { preparation: [...], spraying: [...] }
      // Old format: flat array (backward compat)
      const hasPPE = Array.isArray(data.ppeRequirements)
        ? data.ppeRequirements.length > 0
        : (data.ppeRequirements.preparation?.length > 0 || data.ppeRequirements.spraying?.length > 0);
      if (hasPPE) {
        fieldsToSet.ppe_requirements = data.ppeRequirements;
      }
    }

    if (data.tasks && data.tasks.length > 0 && data.tasks[0].task_name) {
      fieldsToSet.tasks = data.tasks;
    }

    if (Object.keys(fieldsToSet).length > 0) {
      form.setFieldsValue(fieldsToSet);
    }

    // Store permission & task data
    setPendingPermissionTaskData(data);
    setIsPermissionTaskModalVisible(false); // Close modal

    // Check if we need to save as template or update existing template
    if (pendingSprayJobData?.save_as_template) {
      setIsTemplateNameModalVisible(true);
    } else if (isCustomizingTemplate && customizingTemplateId) {
      // Update existing template with new data - pass the latest data
      updateExistingTemplate(data);
    } else {
      // Call API to create spray job directly
      form.submit();
    }
  };

  const handlePermissionTaskModalCancel = () => {
    setIsPermissionTaskModalVisible(false);
  };

  const updateExistingTemplate = async (latestPermissionTaskData) => {
    try {
      setSubmitting(true);

      // Combine current form data with the latest permission/task data
      const currentFormData = form.getFieldsValue();
      const combinedData = {
        ...currentFormData,
        ...(latestPermissionTaskData && {
          ppe_requirements: latestPermissionTaskData.ppeRequirements,
          tasks: latestPermissionTaskData.tasks
        })
      };

      // Remove save_as_template from the data
      delete combinedData.save_as_template;

      const templateData = {
        spray_job_data: combinedData,
        property_id: propertyId
      };

      const response = await sprayService.updateTemplate(customizingTemplateId, templateData);

      if (response && response.code === '0000') {
        message.success('Template updated successfully');
        // Reset customizing state and return to create spray job screen
        resetToCreateSprayJobScreen();
      } else {
        message.error('Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      message.error('Failed to update template');
    } finally {
      setSubmitting(false);
    }
  };

  const createSprayJob = (data) => {
    // Prepare data for API. Strip the client-side _dndId markers from
    // zones / chemicals before persisting — they're only used to give the
    // reorder UI stable React keys and would otherwise pollute the
    // serialized JSON.
    const sprayJobData = {
      ...data,
      zones: stripDndIds(data.zones),
      chemicals: stripDndIds(data.chemicals),
      property_id: propertyId,
      // If the creator didn't pick anyone, default responsibility to themselves.
      responsible_id: data.responsible_id || currentUserId || null,
      save_as_template: 0
    };

    // Call API to create or update spray job
    sprayService.storeOrUpdate(sprayJobData, isEditMode ? editJobId : null)
      .then(response => {
        if (response && response.code === '0000') {
          message.success(isEditMode ? 'Spray job updated successfully' : 'Spray job created successfully');
          history.push('/spray');
        } else {
          message.error(isEditMode ? 'Failed to update spray job' : 'Failed to create spray job');
        }
      })
      .catch(error => {
        console.error('Error creating spray job:', error);
        message.error('Failed to create spray job');
      });
  };

  const resetToCreateSprayJobScreen = () => {
    // Reset all customizing state
    setIsCustomizingTemplate(false);
    setCustomizingTemplateId(null);
    setPendingSprayJobData(null);
    setPendingPermissionTaskData(null);

    // Reset form to empty state
    form.resetFields();
    setZones([ensureDndId({
      startLetter: '', startNumber1: '', startNumber2: '',
      finishLetter: '', finishNumber1: '', finishNumber2: '',
      comment: ''
    })]);
    setChemicals([ensureDndId({ chemical_id: '', quantity: '', rate_unit: '' })]);

    // Open the templates modal
    setIsUseTemplateModalVisible(true);
  };

  const handleCancelCustomization = () => {
    resetToCreateSprayJobScreen();
  };

  const onFinish = (values) => {
    setSubmitting(true);
    createSprayJob(values);
  };

  const onFinishFailed = (errorInfo) => {
    console.error('Failed:', errorInfo);
    message.error('Please check the form for errors');
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    // Skip the positional `_dndId` merge when the change came from a drag
    // handler — the drag-end already wrote the correctly-iddDed state via
    // setZones/setChemicals, and re-running the positional merge here
    // would swap _dndIds (since `prev` in the functional updater can be
    // either the pre- or post-drag state depending on React's batching).
    if (dragSyncRef.current) return;

    // Preserve `_dndId` on each item when syncing from form values back to
    // state — form fields only know about the user-visible attributes, so
    // a naive replace would wipe the stable drag identifiers and break
    // reordering for subsequent edits.
    if (changedValues.zones) {
      setZones((prev) =>
        (allValues.zones || []).map((z, i) => ({ ...z, _dndId: prev[i]?._dndId || makeDndId() }))
      );
    }
    if (changedValues.chemicals) {
      setChemicals((prev) =>
        (allValues.chemicals || []).map((c, i) => ({ ...c, _dndId: prev[i]?._dndId || makeDndId() }))
      );
    }

    // Delta T is now a manual input on the form (Damien) — the
    // operator types the safe spraying range directly. The reactive
    // Stull recompute that used to live here is gone; the Zone-Start
    // gate still uses Stull at spray time to compare the live reading
    // against the operator-set min/max.
  };

  // Helper to get formulation type for a selected chemical
  const getChemicalFormulationType = (chemicalId) => {
    const asset = chemicalAssets.find(c => c.id === chemicalId);
    if (asset && asset.formulation_type) {
      return getFormulationLabel(asset.formulation_type);
    }
    return null;
  };

  return (
    // no-overflow-clip lifts the default `overflow: hidden` on the
    // page wrapper so the sticky page-header inside
    // SprayJobCreateWrapper actually sticks while the form scrolls.
    <LayoutWrapper className="no-overflow-clip">
      <SprayJobCreateWrapper>
        <div className="page-header">
          <h1>{isCustomizingTemplate ? 'Edit Template' : isEditMode ? 'Edit Spray Job' : 'Create New Spray Job'}</h1>
          <div className="action-buttons">
            {!isCustomizingTemplate && (
              <Button type="primary" className="template-btn" onClick={handleUseExistingTemplate}>Use Existing Template</Button>
            )}
            <Button className="cancel-btn" onClick={isCustomizingTemplate ? handleCancelCustomization : () => history.push('/spray')}>Cancel</Button>
            <Button
              type="primary"
              className="continue-btn"
              onClick={handleContinue}
            >
              {isCustomizingTemplate ? 'Update Template' : 'Continue'}
            </Button>
          </div>
        </div>

        <Spin spinning={loading || submitting}>
          <Form
            layout="vertical"
            className="spray-job-form"
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={handleFormValuesChange}
            onKeyDown={handleFormArrowNav}
          >
            {/* All fields in one section */}
            <div className="form-section">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="job_name"
                    label="Job Name"
                    className="isoInputWrapper"
                    rules={[
                      { required: true, whitespace: true, message: 'Job Name is required' }
                    ]}
                  >
                    <Input size="large" placeholder="Job Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="job_type"
                    label="Job Type"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Job Type is required' }]}
                  >
                    <Select size="large" placeholder="Select">
                      {JOB_TYPES.map(type => (
                        <Option key={type.value} value={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="order_number"
                    label="Order number"
                    className="isoInputWrapper"
                    rules={[{ required: false, message: 'Order Number is required' }]}
                  >
                    <Input size="large" placeholder="Enter order number" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="responsible_id"
                    label="Responsible"
                    className="isoInputWrapper"
                    extra="Leave blank to assign yourself."
                  >
                    <Select
                      size="large"
                      placeholder="Select a user (optional)"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={propertyUsers.map((u) => ({
                        value: u.id,
                        label: `${u.name || ''} ${u.surname || ''}`.trim() || u.email || `User #${u.id}`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Chemicals */}
              <Form.Item
                label="Chemicals"
                name="chemicals"
                className="isoInputWrapper"
                rules={[{ required: true, message: 'At least one chemical is required' }]}
              >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleChemicalDragEnd}>
                <SortableContext items={chemicals.map((c) => c._dndId)} strategy={verticalListSortingStrategy}>
                {chemicals.map((chemical, index) => (
                  <SortableItem
                    key={chemical._dndId}
                    id={chemical._dndId}
                    style={{
                      marginBottom: '16px',
                      padding: '16px',
                      border: '1px dashed #d9d9d9',
                      borderRadius: '8px',
                    }}
                  >
                    <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                      <Col span={24}>
                        <Typography.Text strong>Chemical {index + 1}</Typography.Text>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeChemical(index)}
                          style={{ float: 'right' }}
                        />
                      </Col>
                    </Row>

                    <Form.Item className="isoInputWrapper">
                      <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={10} lg={8}>
                          <Form.Item
                            name={['chemicals', index, 'chemical_id']}
                            noStyle
                            rules={[{ required: true, message: '' }]}
                          >
                            <Select
                              placeholder="Select chemical"
                              size="large"
                              showSearch
                              optionFilterProp="children"
                              style={{ width: '100%' }}
                              className="chemical-select"
                              onChange={(chemicalId) => {
                                // Pre-fill the row's rate from the asset's
                                // stored chemical rate so the operator
                                // doesn't have to re-key it. Skips silently
                                // when the asset has no parseable rate.
                                const asset = chemicalAssets.find((a) => a.id === chemicalId);
                                const derived = deriveSprayRateFromAsset(asset);
                                if (!derived) return;
                                const current = form.getFieldValue('chemicals') || [];
                                const next = current.map((c, i) =>
                                  i === index ? { ...c, ...derived } : c
                                );
                                form.setFieldsValue({ chemicals: next });
                              }}
                            >
                              {chemicalAssets.map((option) => (
                                <Option key={option.id} value={option.id}>
                                  {option.title} {option.chemical_batch_number ? `- ${option.chemical_batch_number}` : ''}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8} lg={8}>
                          <Form.Item
                            name={['chemicals', index, 'quantity']}
                            noStyle
                            rules={[{ required: true, message: 'Rate is required' }]}
                          >
                            <InputNumber
                              placeholder="Rate"
                              size="large"
                              min={0}
                              max={1000000}
                              addonAfter={
                                <Form.Item
                                  name={['chemicals', index, 'rate_unit']}
                                  noStyle
                                  rules={[{ required: true, message: 'Unit required' }]}
                                >
                                  <Select size="small" placeholder="Unit" style={{ width: 110 }} suffixIcon={null}>
                                    <Option value="g">g / 100L</Option>
                                    <Option value="mL">mL / 100L</Option>
                                  </Select>
                                </Form.Item>
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item noStyle shouldUpdate={(prev, cur) =>
                        prev?.chemicals?.[index]?.chemical_id !== cur?.chemicals?.[index]?.chemical_id
                      }>
                        {({ getFieldValue }) => {
                          const chemicalId = getFieldValue(['chemicals', index, 'chemical_id']);
                          const formulationType = getChemicalFormulationType(chemicalId);
                          return formulationType ? (
                            <Typography.Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                              Formulation: {formulationType}
                            </Typography.Text>
                          ) : null;
                        }}
                      </Form.Item>
                    </Form.Item>
                  </SortableItem>
                ))}
                </SortableContext>
                </DndContext>

                <div className="chemical-add-button" style={{ marginTop: '8px' }}>
                  <Button type="dashed" size="large" icon={<PlusOutlined />} onClick={addChemical}>
                    Add Chemical
                  </Button>
                </div>
              </Form.Item>

              {/* Equipment */}
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="tractor_id"
                    label="Tractor"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Tractor is required' }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select tractor"
                      onChange={(tractorId) => {
                        // Pre-fill tractor_setting from the asset's saved
                        // dosage field. The Facility schema has a single
                        // generic `dosage` per asset; for tractor assets
                        // that's the default ground speed in km/h. Skips
                        // when the asset has no value saved or when the
                        // operator already typed something they'd want
                        // preserved.
                        const asset = tractors.find((t) => t.id === tractorId);
                        const v = asset?.dosage;
                        if (v != null && v !== '' && !form.getFieldValue('tractor_setting')) {
                          form.setFieldsValue({ tractor_setting: Number(v) });
                        }
                      }}
                    >
                      {tractors.map(tractor => (
                        <Option key={tractor.id} value={tractor.id}>
                          {tractor.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="sprayer_id"
                    label="Sprayer"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Sprayer is required' }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select sprayer"
                      onChange={(sprayerId) => {
                        // Same pattern as tractor — sprayer asset's
                        // `dosage` field carries its default operating
                        // pressure in PSI.
                        const asset = sprayers.find((s) => s.id === sprayerId);
                        const v = asset?.dosage;
                        if (v != null && v !== '' && !form.getFieldValue('sprayer_setting')) {
                          form.setFieldsValue({ sprayer_setting: Number(v) });
                        }
                      }}
                    >
                      {sprayers.map(sprayer => (
                        <Option key={sprayer.id} value={sprayer.id}>
                          {sprayer.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="nozzle_id"
                    label="Nozzle"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Nozzle is required' }]}
                  >
                    <Select size="large" placeholder="Select nozzle">
                      {nozzles.map(nozzle => (
                        <Option key={nozzle.id} value={nozzle.id}>
                          {nozzle.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="tractor_setting"
                    label="Tractor settings (km/h)"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Tractor setting is required' }]}
                  >
                    <InputNumber
                      size="large"
                      min={0.5}
                      max={50}
                      step={0.5}
                      placeholder="0.5-50 km/h"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sprayer_setting"
                    label="Sprayer settings (PSI)"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Sprayer setting is required' }]}
                  >
                    <InputNumber
                      size="large"
                      min={0}
                      max={1000}
                      step={1}
                      placeholder="0-1000 PSI"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Total Spray Volume removed (Damien, round-4 follow-up):
                  the field is fully redundant now that Initial Fill +
                  refill markers fully describe the per-tank water plan.
                  The DB column is left in place so historical jobs still
                  render their stored value, but the create form no
                  longer asks for it. */}

              {/* Environmental Limits */}
              <Form.Item label="Environmental Limits" className="isoInputWrapper">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="wind_limit_min"
                      label="Wind Min:"
                      className="isoInputWrapper"
                      rules={[{ required: true, message: 'Wind min is required' }]}
                    >
                      <InputNumber
                        size="large"
                        min={0}
                        addonAfter="km/h"
                        placeholder="Min"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="wind_limit_max"
                      label="Wind Max:"
                      className="isoInputWrapper"
                      rules={[{ required: true, message: 'Wind max is required' }]}
                    >
                      <InputNumber
                        size="large"
                        min={0}
                        addonAfter="km/h"
                        placeholder="Max"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  {/* Humidity Min/Max — drives the Delta T range below
                      via (100 - humidity)/5. Placed before Delta T so the
                      input flow matches the derivation order. */}
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="humidity_limit_min"
                      label="Humidity Min:"
                      className="isoInputWrapper"
                    >
                      <InputNumber
                        size="large"
                        min={0}
                        max={100}
                        addonAfter="%"
                        placeholder="Min"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="humidity_limit_max"
                      label="Humidity Max:"
                      className="isoInputWrapper"
                    >
                      <InputNumber
                        size="large"
                        min={0}
                        max={100}
                        addonAfter="%"
                        placeholder="Max"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="temperature_min"
                      label="Temperature Min:"
                      className="isoInputWrapper"
                    >
                      <InputNumber
                        size="large"
                        addonAfter="°C"
                        placeholder="Min"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="temperature_max"
                      label="Temperature Max:"
                      className="isoInputWrapper"
                    >
                      <InputNumber
                        size="large"
                        addonAfter="°C"
                        placeholder="Max"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  {/* Delta T is derived from the humidity range above via
                      (100 - humidity)/5; inputs are read-only so the user
                      doesn't manually edit a value that gets overwritten
                      on the next humidity change. */}
                  {/* Delta T is operator-entered now (Damien): set
                      the safe spraying Delta T range directly. The
                      Zone-Start gate runs Stull at spray time to
                      check the live reading against this min/max. */}
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="delta_t_limit_min"
                      label="Delta T Min:"
                      className="isoInputWrapper"
                    >
                      <InputNumber
                        size="large"
                        addonAfter="°C"
                        placeholder="Min"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="delta_t_limit_max"
                      label="Delta T Max:"
                      className="isoInputWrapper"
                    >
                      <InputNumber
                        size="large"
                        addonAfter="°C"
                        placeholder="Max"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="rain_limit"
                      label="Max Rain:"
                      extra="Maximum allowable rainfall during the spray."
                      className="isoInputWrapper"
                      rules={[{ required: true, message: 'Max rain is required' }]}
                    >
                      <InputNumber
                        size="large"
                        min={0}
                        addonAfter="mm/hr"
                        placeholder="Max"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              {/* Initial Fill — the actual amount of water loaded into
                  the tractor for the first spray. Stakeholder rename of
                  water_quantity: operators don't always fill to the
                  tractor's maximum capacity, so the label needs to make
                  it clear we're capturing what was actually filled.
                  Positioned just above the Zones list so the value reads
                  in context with the zones that consume the tank. */}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="water_quantity"
                    label="Initial Fill"
                    className="isoInputWrapper"
                    rules={[{ required: true, message: 'Initial fill is required' }]}
                    extra="Litres loaded into the tractor for the first tank (not always the tractor's maximum capacity). Drives Mix Recipe dose calculations."
                  >
                    <InputNumber
                      size="large"
                      min={1}
                      step={1}
                      addonAfter="L"
                      placeholder="Litres in tractor"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Zones - moved to bottom of form */}
              <Form.Item
                label="Zones"
                name="zones"
                className="isoInputWrapper"
                rules={[{ required: true, message: 'At least one zone is required' }]}
              >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleZoneDragEnd}>
                <SortableContext items={zones.map((z) => z._dndId)} strategy={verticalListSortingStrategy}>
                {(() => {
                  // Counters keep "Zone N" and "Refill N" numbered separately
                  // across the heterogeneous list.
                  let zoneCount = 0;
                  let refillCount = 0;
                  return zones.map((zone, index) => {
                    if (zone && zone.type === 'refill') {
                      refillCount += 1;
                      const refillNum = refillCount;
                      return (
                        <SortableItem
                          key={zone._dndId}
                          id={zone._dndId}
                          // Tinted background so refill tiles are visually
                          // distinct from zone tiles in the same list.
                          style={{
                            marginBottom: '16px',
                            padding: '16px',
                            border: '1px dashed #b7d8c5',
                            borderRadius: '8px',
                            background: '#f0f7f3',
                          }}
                        >
                          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                            <Col span={24}>
                              <Typography.Text strong>Refill {refillNum}</Typography.Text>
                              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                                — tank refill between the zones above and below
                              </Typography.Text>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeZone(index)}
                                style={{ float: 'right' }}
                              />
                            </Col>
                          </Row>
                          <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name={['zones', index, 'liters']}
                                label="Refill Liters"
                                rules={[{ required: true, message: 'Refill liters is required' }]}
                                extra="Litres for the next tank. Becomes the next spray job's Initial Fill."
                              >
                                <InputNumber
                                  size="large"
                                  min={1}
                                  step={1}
                                  addonAfter="L"
                                  placeholder="Litres"
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          {/* Persisted-shape hint so the form value carries
                              the `type: refill` discriminator end-to-end. */}
                          <Form.Item name={['zones', index, 'type']} hidden initialValue="refill">
                            <Input type="hidden" />
                          </Form.Item>
                        </SortableItem>
                      );
                    }
                    zoneCount += 1;
                    const zoneNum = zoneCount;
                    return (
                  <SortableItem
                    key={zone._dndId}
                    id={zone._dndId}
                    style={{ marginBottom: '16px', padding: '16px', border: '1px dashed #d9d9d9', borderRadius: '8px' }}
                  >
                    <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                      <Col span={24}>
                        <Typography.Text strong>Zone {zoneNum}</Typography.Text>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeZone(index)}
                          style={{ float: 'right' }}
                        />
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          label="Start Zone"
                          className="isoInputWrapper"
                        >
                          <Row gutter={[8, 8]}>
                            <Col span={8}>
                              <Form.Item
                                name={['zones', index, 'startLetter']}
                                label="FIELD"
                                rules={[{ required: true, whitespace: true, message: 'Field is required' }]}
                              >
                                <Input
                                  ref={(el) => { zoneFieldRefs.current[index] = el; }}
                                  size="large"
                                  placeholder="e.g. A, K6, PD"
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                name={['zones', index, 'startNumber1']}
                                label="BENCH"
                                rules={[{ required: false }]}
                              >
                                <InputNumber size="large" min={1} max={100} placeholder="1-100" inputMode="numeric" pattern="[0-9]*" style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                name={['zones', index, 'startNumber2']}
                                label="ROW"
                                rules={[{ required: false }]}
                              >
                                <InputNumber size="large" min={1} max={999} placeholder="1-200" inputMode="numeric" pattern="[0-9]*" style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          label="Finish Zone"
                          className="isoInputWrapper"
                        >
                          <Row gutter={[8, 8]}>
                            <Col span={8}>
                              <Form.Item
                                name={['zones', index, 'finishNumber2']}
                                label="ROW"
                                rules={[{ required: false }]}
                              >
                                <InputNumber size="large" min={1} max={999} placeholder="1-200" inputMode="numeric" pattern="[0-9]*" style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Form.Item
                          name={['zones', index, 'comment']}
                          label="Comments"
                        >
                          <Input.TextArea rows={2} placeholder="e.g. spray k6.south row 1 and 2 all brassicas" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </SortableItem>
                    );
                  });
                })()}
                </SortableContext>
                </DndContext>

                <div className="zone-add-button" style={{ marginTop: '8px', display: 'flex', gap: 8 }}>
                  <Button type="dashed" size="large" icon={<PlusOutlined />} onClick={addZone}>
                    Add zone
                  </Button>
                  <Button type="dashed" size="large" icon={<PlusOutlined />} onClick={addRefill}>
                    Add refill
                  </Button>
                </div>
              </Form.Item>

              {/* Refill Prep section (checkbox + Total Spray Volume input)
                  was removed in favour of refill tiles in the zones list.
                  refill_planned is now derived server-side from the
                  presence of any refill items in the zones JSON. */}

              {/* Save as Template */}
              {!isCustomizingTemplate && (
                  <Form.Item
                      name="save_as_template"
                      valuePropName="checked"
                      className="isoInputWrapper"
                  >
                    <Checkbox>Save this setup as a template for future use.</Checkbox>
                  </Form.Item>
              )}

              {/* Hidden fields for modal data */}
              <Form.Item name="ppe_requirements" hidden>
                <Input />
              </Form.Item>

              <Form.Item name="tasks" hidden>
                <Input />
              </Form.Item>
            </div>
          </Form>
        </Spin>

        {/* Modal */}
        <PermissionTaskModal
          visible={isPermissionTaskModalVisible}
          onCancel={handlePermissionTaskModalCancel}
          onSave={handlePermissionTaskModalSave}
          initialData={pendingPermissionTaskData}
        />

        <TemplateNameModal
          visible={isTemplateNameModalVisible}
          onCancel={() => setIsTemplateNameModalVisible(false)}
          onSave={handleTemplateNameModalSave}
          sprayJobData={pendingSprayJobData}
          permissionTaskData={pendingPermissionTaskData}
        />

        <UseExistingTemplateModal
          visible={isUseTemplateModalVisible}
          onCancel={() => setIsUseTemplateModalVisible(false)}
          onUseTemplate={handleUseTemplate}
          onCustomizeTemplate={handleCustomizeTemplate}
        />
      </SprayJobCreateWrapper>
    </LayoutWrapper>
  );
};

export default SprayJobCreate;
